import {useState} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import axios from 'axios';
import {Loading, Notify} from 'notiflix';
import {useSelector} from 'react-redux';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import {ENDPOINT} from 'constants';

// "Price options hint" button (Dynamic Filters header) + dialog. The text is
// the note just above the plan cards on every page that has price option
// dropdowns (value pack and product detail pages), one text for all modules.
// HTML allowed; an empty text hides the note on the site.
const PriceOptionHintSettings = () => {
  const {user} = useSelector(state => state.auth);
  const authHeaders = {headers: {Authorization: `Bearer ${user?.accessToken}`}};
  const api = `${ENDPOINT}api/v1/admin/price-options/hint`;

  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState({pending: '', done: ''});
  const [saving, setSaving] = useState(false);

  const openDialog = async () => {
    try {
      Loading.circle('Loading...');
      const {data} = await axios.get(api, authHeaders);
      setHint({pending: data?.data?.pending || '', done: data?.data?.done || ''});
      setOpen(true);
    } catch {
      Notify.failure('Failed to load the price options hint');
    } finally {
      Loading.remove();
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      const {data} = await axios.put(api, hint, authHeaders);
      setHint({pending: data?.data?.pending || '', done: data?.data?.done || ''});
      Notify.success('Price options hint saved');
      setOpen(false);
    } catch (error) {
      Notify.failure(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <MDButton variant="outlined" color="info" size="small" onClick={openDialog}>
        <Icon sx={{mr: 0.5}}>tips_and_updates</Icon>
        Price options hint
      </MDButton>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Price options hint</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" display="block" mb={2}>
            The note above the plan cards on every page with price option
            dropdowns (value packs and products). HTML allowed, e.g.
            &lt;strong&gt;bold&lt;/strong&gt;. Leave a box empty to hide that
            note on the site.
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Before the questions are answered"
                value={hint.pending}
                onChange={e => setHint(prev => ({...prev, pending: e.target.value}))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="After every question is answered"
                value={hint.done}
                onChange={e => setHint(prev => ({...prev, done: e.target.value}))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDBox display="flex" gap={1}>
            <MDButton
              variant="outlined"
              color="secondary"
              disabled={saving}
              onClick={() => setOpen(false)}>
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="info" disabled={saving} onClick={save}>
              {saving ? 'Saving...' : 'Save hint'}
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PriceOptionHintSettings;
